<template>
  <div class="notif-settings">
    <!-- Header -->
    <div class="notif-header">
      <router-link
        :to="{ name: 'profile' }"
        class="notif-back"
      >
        <Icon type="ios-arrow-back" />
        {{ $t("Back to profile") }}
      </router-link>
      <h2 class="notif-title">
        <Icon
          type="md-notifications"
          class="notif-title__icon"
        />
        {{ $t("Notification preferences") }}
      </h2>
      <p class="notif-subtitle">
        {{ $t("Choose which notifications you receive and how far in advance.") }}
      </p>
    </div>

    <Spin
      v-if="loading"
      fix
    />

    <!-- Traveler family -->
    <Card
      class="pref-card"
      :class="{ 'pref-card--off': !prefs.traveler.enabled }"
      dis-hover
    >
      <div class="pref-head">
        <span class="pref-icon pref-icon--traveler">
          <Icon type="ios-plane" />
        </span>
        <div class="pref-head__text">
          <p class="pref-title">
            {{ $t("My trips") }}
            <span
              class="pref-status"
              :class="prefs.traveler.enabled ? 'pref-status--on' : 'pref-status--off'"
            >{{ prefs.traveler.enabled ? $t("Enabled") : $t("Disabled") }}</span>
          </p>
          <p class="pref-help">
            {{ $t("Notifications about the countries you travel to.") }}
          </p>
        </div>
        <i-switch
          v-model="prefs.traveler.enabled"
          class="pref-switch"
        />
      </div>

      <div
        v-if="prefs.traveler.enabled"
        class="pref-body"
      >
        <div class="pref-field">
          <span class="pref-field-label">{{ $t("Days before departure") }}</span>
          <InputNumber
            v-model="prefs.traveler.days_before"
            :min="0"
            :max="60"
          />
        </div>
      </div>

      <div class="pref-actions">
        <Button
          type="primary"
          :loading="saving.traveler"
          @click="save('traveler')"
        >
          {{ $t("Save") }}
        </Button>
      </div>
    </Card>

    <!-- Manager family -->
    <Card
      class="pref-card"
      :class="{ 'pref-card--off': !prefs.manager.enabled }"
      dis-hover
    >
      <div class="pref-head">
        <span class="pref-icon pref-icon--manager">
          <Icon type="md-people" />
        </span>
        <div class="pref-head__text">
          <p class="pref-title">
            {{ $t("My teams' trips") }}
            <span
              class="pref-status"
              :class="prefs.manager.enabled ? 'pref-status--on' : 'pref-status--off'"
            >{{ prefs.manager.enabled ? $t("Enabled") : $t("Disabled") }}</span>
          </p>
          <p class="pref-help">
            {{ $t("Trips of your entity and all its child entities (hierarchical).") }}
          </p>
        </div>
        <i-switch
          v-model="prefs.manager.enabled"
          class="pref-switch"
        />
      </div>

      <div
        v-if="prefs.manager.enabled"
        class="pref-body"
      >
        <div class="pref-field">
          <span class="pref-field-label">{{ $t("Days before departure") }}</span>
          <InputNumber
            v-model="prefs.manager.days_before"
            :min="0"
            :max="60"
          />
        </div>
      </div>

      <div class="pref-actions">
        <Button
          type="primary"
          :loading="saving.manager"
          @click="save('manager')"
        >
          {{ $t("Save") }}
        </Button>
      </div>
    </Card>

    <!-- Zone family -->
    <Card
      class="pref-card"
      :class="{ 'pref-card--off': !prefs.zone.enabled }"
      dis-hover
    >
      <div class="pref-head">
        <span class="pref-icon pref-icon--zone">
          <Icon type="md-globe" />
        </span>
        <div class="pref-head__text">
          <p class="pref-title">
            {{ $t("Followed zones & countries") }}
            <span
              class="pref-status"
              :class="prefs.zone.enabled ? 'pref-status--on' : 'pref-status--off'"
            >{{ prefs.zone.enabled ? $t("Enabled") : $t("Disabled") }}</span>
          </p>
          <p class="pref-help">
            {{ $t("Opt-in: receive notifications for the zones or countries you choose.") }}
          </p>
        </div>
        <i-switch
          v-model="prefs.zone.enabled"
          class="pref-switch"
        />
      </div>

      <div
        v-if="prefs.zone.enabled"
        class="pref-body"
      >
        <div class="pref-field pref-field--column">
          <span class="pref-field-label">{{ $t("Zones") }}</span>
          <ZonePicker v-model="prefs.zone.subscribed_zones" />
        </div>
        <div class="pref-field pref-field--column">
          <span class="pref-field-label">{{ $t("Countries") }}</span>
          <CountryPicker v-model="prefs.zone.subscribed_countries" />
        </div>
      </div>

      <div class="pref-actions">
        <Button
          type="primary"
          :loading="saving.zone"
          @click="save('zone')"
        >
          {{ $t("Save") }}
        </Button>
      </div>
    </Card>
  </div>
</template>

<script>
import {
  getNotificationPreferencesQry,
  postNotificationPreferenceQry,
  patchNotificationPreferenceQry,
} from "@/services/notification.service";
import CountryPicker from "@/views/organization/CountryPicker.vue";
import ZonePicker from "@/views/organization/ZonePicker.vue";

// Backend defaults (SNEF framing): traveler/manager on, zone off; X = 3.
const DEFAULTS = {
  traveler: { enabled: true, days_before: 3, subscribed_zones: [], subscribed_countries: [] },
  manager: { enabled: true, days_before: 3, subscribed_zones: [], subscribed_countries: [] },
  zone: { enabled: false, days_before: 3, subscribed_zones: [], subscribed_countries: [] },
};

export default {
  name: "NotificationSettings",
  components: { CountryPicker, ZonePicker },
  data() {
    return {
      loading: false,
      saving: { traveler: false, manager: false, zone: false },
      // id of the persisted preference row per family (null if not saved yet).
      ids: { traveler: null, manager: null, zone: null },
      prefs: JSON.parse(JSON.stringify(DEFAULTS)),
    };
  },
  created() {
    this.organizationStore.fetchZones();
    this.fetchPreferences();
  },
  methods: {
    async fetchPreferences() {
      this.loading = true;
      try {
        const res = await getNotificationPreferencesQry();
        const rows = Array.isArray(res.data) ? res.data : res.data.results || [];
        // Start from defaults, overlay any saved preference.
        this.prefs = JSON.parse(JSON.stringify(DEFAULTS));
        this.ids = { traveler: null, manager: null, zone: null };
        rows.forEach((row) => {
          if (this.prefs[row.family]) {
            this.ids[row.family] = row.id;
            this.prefs[row.family] = {
              enabled: row.enabled,
              days_before: row.days_before != null ? row.days_before : DEFAULTS[row.family].days_before,
              subscribed_zones: row.subscribed_zones || [],
              subscribed_countries: row.subscribed_countries || [],
            };
          }
        });
      } finally {
        this.loading = false;
      }
    },

    async save(family) {
      this.saving[family] = true;
      const p = this.prefs[family];
      const payload = {
        family,
        enabled: p.enabled,
        days_before: p.days_before,
      };
      // Only the zone family carries subscriptions.
      if (family === "zone") {
        payload.subscribed_zones = p.subscribed_zones;
        payload.subscribed_countries = p.subscribed_countries;
      }
      try {
        if (this.ids[family]) {
          await patchNotificationPreferenceQry(this.ids[family], payload);
        } else {
          const res = await postNotificationPreferenceQry(payload);
          this.ids[family] = res.data.id;
        }
        this.$Message.success(this.$t("Saved"));
      } catch (err) {
        const data = err && err.response && err.response.data;
        this.$Message.error((data && data.detail) || this.$t("Something went wrong"));
      } finally {
        this.saving[family] = false;
      }
    },
  },
};
</script>

<style scoped>
.notif-settings {
  padding: 24px;
  position: relative;
  max-width: 760px;
  margin: 0 auto;
  background: #f5f7fa;
  min-height: 100%;
}

/* ---- Header ---- */
.notif-header {
  margin-bottom: 20px;
}
.notif-back {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #808695;
  font-size: 13px;
  margin-bottom: 12px;
  transition: color 0.15s ease;
}
.notif-back:hover {
  color: #2d8cf0;
}
.notif-title {
  display: flex;
  align-items: center;
  font-size: 22px;
  font-weight: 600;
  color: #1f2d3d;
  margin: 0;
}
.notif-title__icon {
  font-size: 24px;
  color: #2d8cf0;
  margin-right: 10px;
}
.notif-subtitle {
  margin: 6px 0 0 34px;
  color: #808695;
  font-size: 13px;
}

/* ---- Preference cards ---- */
.pref-card {
  margin-bottom: 16px;
  border: none;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
  transition: opacity 0.15s ease;
}
.pref-card--off {
  opacity: 0.72;
}
.pref-card >>> .ivu-card-body {
  padding: 20px 24px;
}

/* ---- Card head ---- */
.pref-head {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}
.pref-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 10px;
  font-size: 22px;
  flex: 0 0 auto;
}
.pref-icon--traveler {
  background: #e8f4ff;
  color: #2d8cf0;
}
.pref-icon--manager {
  background: #f3edff;
  color: #8e5cf7;
}
.pref-icon--zone {
  background: #e8fbef;
  color: #19be6b;
}
.pref-head__text {
  flex: 1 1 auto;
  min-width: 0;
}
.pref-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  font-size: 16px;
  color: #1f2d3d;
  margin: 2px 0 2px;
}
.pref-status {
  font-size: 11px;
  font-weight: 600;
  padding: 1px 8px;
  border-radius: 10px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.pref-status--on {
  background: #e8fbef;
  color: #19be6b;
}
.pref-status--off {
  background: #f0f2f5;
  color: #808695;
}
.pref-help {
  color: #808695;
  font-size: 13px;
  margin: 0;
}
.pref-switch {
  margin-top: 4px;
  flex: 0 0 auto;
}

/* ---- Card body ---- */
.pref-body {
  margin-top: 18px;
  padding-top: 18px;
  padding-left: 56px;
  border-top: 1px solid #f0f2f5;
}
.pref-field {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}
.pref-field:last-child {
  margin-bottom: 0;
}
.pref-field--column {
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
}
.pref-field-label {
  min-width: 170px;
  color: #515a6e;
  font-size: 13px;
  font-weight: 500;
}

/* ---- Actions ---- */
.pref-actions {
  margin-top: 18px;
  padding-top: 16px;
  padding-left: 56px;
  border-top: 1px solid #f0f2f5;
  display: flex;
  justify-content: flex-end;
}
</style>
